import React, { useMemo, useEffect } from "react";
import { XAxis, XAxisProps, YAxis, YAxisProps } from "recharts";
import { ColorScheme } from "./interface";
import { useResolvedColor, useFontSizeInPx } from "./utils";

export interface ChartXAxisProps extends Partial<XAxisProps> {
  dataKey?: string;
  angle?: number;
  textAnchor?: "end" | "inherit" | "start" | "middle";
  height?: number;
  interval?: number | "preserveStartEnd" | "preserveStart" | "preserveEnd";
  tickFormatter?: (value: any, index: number) => string;
  colorScheme: ColorScheme;
  styleId?: string;
}

export interface ChartYAxisProps extends Partial<YAxisProps> {
  allowDecimals?: boolean;
  domain?: [number | string, number | string];
  colorScheme: ColorScheme;
  styleId?: string;
}

export const ChartXAxis: React.FC<ChartXAxisProps> = ({
  colorScheme,
  styleId,
  ...xAxisProps
}) => {
  const fontSizePx = useFontSizeInPx(colorScheme.xAxis.tick.fontSize);
  const tickFill = useResolvedColor(colorScheme.xAxis.tick.fill);
  const axisLineStroke = useResolvedColor(colorScheme.xAxis.axisLine.stroke);

  useEffect(() => {
    if (!styleId) return;
    const styleId_ = `recharts-xaxis-fontsize-${styleId}`;
    if (document.getElementById(styleId_)) return;

    const style = document.createElement("style");
    style.id = styleId_;
    style.textContent = `.recharts-xAxis .recharts-cartesian-axis-tick-value { font-size: ${fontSizePx}px !important; }`;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(styleId_);
      if (el) document.head.removeChild(el);
    };
  }, [fontSizePx, styleId]);

  const defaultTickFormatter = (value: string) =>
    value?.length > 15 ? `${value.substring(0, 15)}...` : value;

  const defaultProps: XAxisProps = useMemo(
    () => ({
      angle: -60,
      textAnchor: "end",
      height: 100,
      interval: 0,
      tickFormatter: defaultTickFormatter,
      ...xAxisProps,
      tick:
        xAxisProps?.tick &&
        (typeof xAxisProps.tick === "function" ||
          React.isValidElement(xAxisProps.tick))
          ? xAxisProps.tick
          : {
              fill: tickFill,
              fontSize: fontSizePx,
              ...(xAxisProps?.tick &&
                typeof xAxisProps.tick === "object" &&
                !("type" in xAxisProps.tick) &&
                (() => {
                  const {
                    fontSize: _,
                    fill: __,
                    ...rest
                  } = xAxisProps.tick as any;
                  return rest;
                })()),
            },
      axisLine: {
        stroke: axisLineStroke,
        ...(xAxisProps?.axisLine && typeof xAxisProps.axisLine === "object"
          ? xAxisProps.axisLine
          : {}),
      },
    }),
    [tickFill, axisLineStroke, fontSizePx, xAxisProps]
  );

  return <XAxis {...defaultProps} />;
};

export const ChartYAxis: React.FC<ChartYAxisProps> = ({
  colorScheme,
  styleId,
  ...yAxisProps
}) => {
  const fontSizePx = useFontSizeInPx(colorScheme.yAxis.tick.fontSize);
  const tickFill = useResolvedColor(colorScheme.yAxis.tick.fill);
  const axisLineStroke = useResolvedColor(colorScheme.yAxis.axisLine.stroke);

  useEffect(() => {
    if (!styleId) return;
    const styleId_ = `recharts-yaxis-fontsize-${styleId}`;
    if (document.getElementById(styleId_)) return;

    const style = document.createElement("style");
    style.id = styleId_;
    style.textContent = `.recharts-yAxis .recharts-cartesian-axis-tick-value { font-size: ${fontSizePx}px !important; }`;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(styleId_);
      if (el) document.head.removeChild(el);
    };
  }, [fontSizePx, styleId]);

  const defaultProps: YAxisProps = useMemo(
    () => ({
      allowDecimals: false,
      ...yAxisProps,
      tick:
        yAxisProps?.tick &&
        (typeof yAxisProps.tick === "function" ||
          React.isValidElement(yAxisProps.tick))
          ? yAxisProps.tick
          : {
              fill: tickFill,
              fontSize: fontSizePx,
              ...(yAxisProps?.tick &&
                typeof yAxisProps.tick === "object" &&
                !("type" in yAxisProps.tick) &&
                (() => {
                  const {
                    fontSize: _,
                    fill: __,
                    ...rest
                  } = yAxisProps.tick as any;
                  return rest;
                })()),
            },
      axisLine: {
        stroke: axisLineStroke,
        ...(yAxisProps?.axisLine && typeof yAxisProps.axisLine === "object"
          ? yAxisProps.axisLine
          : {}),
      },
    }),
    [tickFill, axisLineStroke, fontSizePx, yAxisProps]
  );

  return <YAxis {...defaultProps} />;
};
